import React, { useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase';
import { useToastContext, ToastVariant } from '../../context';
import { fetchContactSettings, saveContactSettings } from '../../services/partner/partner.api';
import { Button } from '../../components/UI/Button';
import { Card } from '../../components/UI/Card';
import { PageLayout } from '../../layout/PageLayout';
import { Trash2, Plus, Edit, Mail } from 'lucide-react';
import { useUserPartnerInformation } from '../../hooks';

interface QuestionType {
    id: string;
    label: string;
    value: string;
    destinationEmail: string;
}

const toCamelCase = (str: string) => {
    return str
        .toLowerCase()
        .replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());
};

const ContactFormSettings = () => {
    const [user] = useAuthState(auth);
    const { addToast } = useToastContext();
    const [loading, setLoading] = useState(false);
    const [types, setTypes] = useState<QuestionType[]>([]);

    // Modal State
    const [isEditing, setIsEditing] = useState(false);
    const [editingType, setEditingType] = useState<Partial<QuestionType>>({});

    const { userPartnerInformation } = useUserPartnerInformation(user?.uid);
    const partnerId = userPartnerInformation?.data?.partnerId;

    useEffect(() => {
        if (partnerId) {
            loadSettings();
        }
    }, [partnerId]);

    const loadSettings = async () => {
        if (!partnerId) return;
        setLoading(true);
        try {
            const res: any = await fetchContactSettings(partnerId);
            if (res && res.data && res.data.types) {
                setTypes(res.data.types);
            } else {
                setTypes([]);
            }
        } catch (error) {
            console.error('Failed to load settings', error);
            addToast(ToastVariant.ERROR, { title: "Failed to load settings" });
        } finally {
            setLoading(false);
        }
    };

    const handleSaveType = async () => {
        if (!editingType.label || !editingType.destinationEmail) {
            addToast(ToastVariant.ERROR, { title: "Label and Email are required" });
            return;
        }

        const newType = {
            id: editingType.id || crypto.randomUUID(),
            label: editingType.label,
            value: toCamelCase(editingType.label),
            destinationEmail: editingType.destinationEmail
        };

        const updatedTypes = editingType.id
            ? types.map(t => t.id === newType.id ? newType : t)
            : [...types, newType];

        await saveSettings(updatedTypes);
        setIsEditing(false);
    };

    const handleDeleteType = async (typeId: string) => {
        if (!window.confirm("Are you sure you want to delete this question type?")) return;
        const updatedTypes = types.filter(t => t.id !== typeId);
        await saveSettings(updatedTypes);
    };

    const saveSettings = async (newTypes: QuestionType[]) => {
        if (!partnerId) return;
        try {
            await saveContactSettings(partnerId, { types: newTypes });
            setTypes(newTypes);
            addToast(ToastVariant.SUCCESS, { title: "Settings saved" });
        } catch (error) {
            console.error('Failed to save settings', error);
            addToast(ToastVariant.ERROR, { title: "Failed to save settings" });
        }
    }

    return (
        <PageLayout>
            <div className="flex flex-col gap-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold">Contact Form Settings</h1>
                        <p className="text-gray-500 text-sm mt-1">
                            Configure the types of questions customers can ask and where those emails should be sent.
                        </p>
                    </div>
                    <Button onClick={() => { setEditingType({}); setIsEditing(true); }}>
                        <Plus size={16} className="mr-2" />
                        Add Question Type
                    </Button>
                </div>

                <div className="grid gap-4">
                    {types.map(type => (
                        <Card key={type.id} className="flex justify-between items-center p-4">
                            <div>
                                <h3 className="font-semibold text-lg">{type.label}</h3>
                                <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                                    <Mail size={14} />
                                    {type.destinationEmail}
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => { setEditingType(type); setIsEditing(true); }} className="p-2 text-gray-500 hover:text-blue-600 transition">
                                    <Edit size={18} />
                                </button>
                                <button onClick={() => handleDeleteType(type.id)} className="p-2 text-gray-500 hover:text-red-600 transition">
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        </Card>
                    ))}

                    {types.length === 0 && !loading && (
                        <div className="text-center py-12 text-gray-400 bg-gray-50 rounded-lg">
                            No question types defined. Click "Add Question Type" to create one.
                        </div>
                    )}
                </div>

                {/* Edit Modal */}
                {isEditing && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setIsEditing(false)}>
                        <div className="bg-white rounded-lg p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
                            <h2 className="text-xl font-bold mb-4">{editingType.id ? 'Edit Question Type' : 'New Question Type'}</h2>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Type of Question</label>
                                    <input
                                        type="text"
                                        className="w-full border rounded px-3 py-2"
                                        placeholder="e.g. Repairs, Sales, General Inquiry"
                                        value={editingType.label || ''}
                                        onChange={e => setEditingType({ ...editingType, label: e.target.value })}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Destination Email</label>
                                    <input
                                        type="email"
                                        className="w-full border rounded px-3 py-2"
                                        placeholder="e.g. repairs@partner.com"
                                        value={editingType.destinationEmail || ''}
                                        onChange={e => setEditingType({ ...editingType, destinationEmail: e.target.value })}
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Emails for this question type will be sent here.</p>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-6">
                                <Button variant="secondary" onClick={() => setIsEditing(false)}>Cancel</Button>
                                <Button onClick={handleSaveType}>Save</Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </PageLayout>
    );
};

export default ContactFormSettings;
